import {useSearchParams} from '@github-ui/use-navigate'
import {useState, useMemo, useCallback} from 'react'
import type {FilterQuery} from '@github-ui/filter-core'
import type {Icon} from '@primer/octicons-react'
import {parsePageNumber} from '../utils/url-helpers'
import {
  SessionStatusFilterProvider,
  SessionCreatorFilterProvider,
  SessionSortFilterProvider,
  SessionDetailedStatusFilterProvider,
  SessionAgentFilterProvider,
  SessionTypeFilterProvider,
} from '../utils/session-filter-providers'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import type {SubAgent} from '@github-ui/agent-sessions-types/sub-agent'
import type {ActiveTab} from '../types/filter-types'
import type {SortDirection, ParsedFilters} from '../utils/filter-utils'
import {DEFAULT_FILTER_QUERY, ME_ALIAS, parseFilterQuery, buildFilterQuery} from '../utils/filter-utils'
import {useCreatorOptions, useAgentOptions} from './use-filter-options'
import {
  readFiltersFromSearchParams,
  writeFiltersToSearchParams,
  writeLegacyFilterParams,
} from '../utils/session-filter-url'

export interface UseTaskFiltersOptions {
  /** The active status tab (open/closed/all) driving default status qualifiers. */
  activeTab: ActiveTab
  /** Whether tasks are currently being fetched (used to detect in-flight navigation). */
  isFetching?: boolean
  currentUserLogin?: string
  apiAgents?: AgentInfo[]
  customAgents?: SubAgent[]
  apiCreators?: Array<{id: number; login?: string}>
  /** Hide the author filter key from filter-bar suggestions while keeping the provider (for @me highlighting). */
  hideAuthorFilter?: boolean
  /** Use the unified `q=` search param syntax instead of legacy individual params. */
  useQSyntax?: boolean
  /** Fully omit the author/creator filter provider (e.g. /copilot/agents, which is always scoped to the current user). */
  omitAuthorProvider?: boolean
  /**
   * Overrides the reset/default query so a view's implicit filters survive a reset.
   * e.g. the Needs Attention view passes `is:open status:needs_attention` so changing
   * the repo (which resets to default) doesn't drop the needs-attention qualifier.
   */
  defaultQuery?: string
}

export function useTaskFilters({
  activeTab,
  isFetching = false,
  currentUserLogin,
  apiAgents,
  customAgents,
  apiCreators,
  hideAuthorFilter,
  useQSyntax,
  omitAuthorProvider,
  defaultQuery,
}: UseTaskFiltersOptions) {
  const [searchParams, setSearchParams] = useSearchParams()

  // --- Resolve @me helper ---
  const resolveMe = useCallback(
    (value: string) => (value === ME_ALIAS && currentUserLogin ? currentUserLogin : value),
    [currentUserLogin],
  )

  // --- Read filter state from unified ?q= param (with legacy fallback) ---
  const parsedFromUrl = useMemo(
    () => readFiltersFromSearchParams(searchParams, currentUserLogin),
    [searchParams, currentUserLogin],
  )

  const sortDirection: SortDirection = parsedFromUrl.sort
  const selectedCreators = parsedFromUrl.creators
  const excludedCreators = parsedFromUrl.excludedCreators
  const selectedAgents = parsedFromUrl.agents
  const selectedTypes = parsedFromUrl.types
  const selectedStatuses = parsedFromUrl.detailedStatuses
  const searchQuery = parsedFromUrl.freeText

  // --- Canonical filter string built from URL params ---

  const canonicalFilterValue = useMemo(
    () =>
      buildFilterQuery(
        activeTab,
        selectedCreators,
        sortDirection,
        searchQuery,
        selectedStatuses,
        selectedAgents,
        currentUserLogin,
        excludedCreators,
        selectedTypes,
      ),
    [
      activeTab,
      selectedCreators,
      sortDirection,
      searchQuery,
      selectedStatuses,
      selectedAgents,
      currentUserLogin,
      excludedCreators,
      selectedTypes,
    ],
  )

  // Sync local filter value when canonical (URL-derived) value changes.
  // This follows React's "adjusting state from props" pattern:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [filterValue, setFilterValue] = useState(canonicalFilterValue)
  const [prevCanonical, setPrevCanonical] = useState(canonicalFilterValue)

  if (canonicalFilterValue !== prevCanonical) {
    setPrevCanonical(canonicalFilterValue)
    setFilterValue(canonicalFilterValue)
  }

  // --- Derive filter options & build filter providers ---

  const creatorOptions = useCreatorOptions(apiCreators, currentUserLogin)
  const agentOptions = useAgentOptions(apiAgents, customAgents)

  // Resolve an agent token to its canonical slug. The token may be a display
  // label ("Copilot") or an already-canonical slug ("copilot-coding-agent");
  // either way we store the slug so query/URL forms stay consistent.
  const resolveAgentSlug = useCallback(
    (agent: string) => agentOptions.find(o => o.label === agent || o.value === agent)?.value ?? agent,
    [agentOptions],
  )

  // Exclude already-selected agents from the suggestion list so users
  // can't pick the same agent twice from the filter bar dropdown.
  const unselectedAgentOptions = useMemo(
    () => agentOptions.filter(option => !selectedAgents.includes(option.value)),
    [agentOptions, selectedAgents],
  )

  const filterProviders = useMemo(
    () =>
      [
        new SessionStatusFilterProvider(),
        new SessionDetailedStatusFilterProvider(activeTab),
        omitAuthorProvider
          ? null
          : new SessionCreatorFilterProvider(
              {currentUserLogin, showAtMe: true},
              {hiddenFromSuggestions: hideAuthorFilter},
            ),
        new SessionTypeFilterProvider(),
        new SessionAgentFilterProvider(
          unselectedAgentOptions.map(option => ({key: option.value, label: option.label, icon: option.icon as Icon})),
        ),
        new SessionSortFilterProvider(),
      ].filter(provider => provider !== null),
    [unselectedAgentOptions, activeTab, hideAuthorFilter, currentUserLogin, omitAuthorProvider],
  )

  // --- URL update helpers ---

  const applyFilters = useCallback(
    (parsed: ParsedFilters) => {
      setSearchParams(
        prev => {
          const resolved: ParsedFilters = {
            ...parsed,
            creators: parsed.creators.map(resolveMe),
            excludedCreators: parsed.excludedCreators.map(resolveMe),
          }
          return useQSyntax
            ? writeFiltersToSearchParams(resolved, prev, currentUserLogin)
            : writeLegacyFilterParams(resolved, prev, currentUserLogin)
        },
        {preventAutofocus: true},
      )
    },
    [setSearchParams, resolveMe, currentUserLogin, useQSyntax],
  )

  const currentFilters = useCallback(
    (overrides: Partial<ParsedFilters> = {}): ParsedFilters => ({
      status: activeTab,
      detailedStatuses: selectedStatuses,
      creators: selectedCreators,
      excludedCreators,
      agents: selectedAgents,
      types: selectedTypes,
      sort: sortDirection,
      freeText: searchQuery,
      ...overrides,
    }),
    [
      activeTab,
      selectedStatuses,
      selectedCreators,
      excludedCreators,
      selectedAgents,
      selectedTypes,
      sortDirection,
      searchQuery,
    ],
  )

  // --- Filter bar callbacks ---

  const handleFilterChange = useCallback((value: string) => {
    setFilterValue(value)
  }, [])

  const handleFilterSubmit = useCallback(
    (request: FilterQuery) => {
      const raw = request.raw.trim()
      const parsed = raw ? parseFilterQuery(raw) : parseFilterQuery('')
      parsed.creators = parsed.creators.map(resolveMe)
      parsed.excludedCreators = parsed.excludedCreators.map(resolveMe)
      parsed.agents = parsed.agents.map(resolveAgentSlug)
      setFilterValue(
        buildFilterQuery(
          parsed.status,
          parsed.creators,
          parsed.sort,
          parsed.freeText,
          parsed.detailedStatuses,
          parsed.agents,
          currentUserLogin,
          parsed.excludedCreators,
          parsed.types,
        ),
      )
      applyFilters(parsed)
    },
    [applyFilters, resolveMe, currentUserLogin, resolveAgentSlug],
  )

  // Context-aware default: include author:@me when viewing own sessions
  const isCreatedByMe = currentUserLogin
    ? selectedCreators.length === 1 && selectedCreators[0] === currentUserLogin
    : false

  // Canonicalize the default query through the same parse -> build pipeline that
  // produces `canonicalFilterValue`, so `isNonDefault` compares like-for-like
  // regardless of the qualifier order/spacing a caller passes in `defaultQuery`,
  // and `handleResetToDefault` always resets to a canonical string.
  const defaultFilterQuery = useMemo(() => {
    const raw = defaultQuery ?? (isCreatedByMe ? `${DEFAULT_FILTER_QUERY} author:${ME_ALIAS}` : DEFAULT_FILTER_QUERY)
    const parsed = parseFilterQuery(raw)
    parsed.creators = parsed.creators.map(resolveMe)
    parsed.excludedCreators = parsed.excludedCreators.map(resolveMe)
    // Default queries rarely include agent qualifiers, so only resolve when present.
    if (parsed.agents.length > 0) parsed.agents = parsed.agents.map(resolveAgentSlug)
    return buildFilterQuery(
      parsed.status,
      parsed.creators,
      parsed.sort,
      parsed.freeText,
      parsed.detailedStatuses,
      parsed.agents,
      currentUserLogin,
      parsed.excludedCreators,
      parsed.types,
    )
  }, [defaultQuery, isCreatedByMe, resolveMe, resolveAgentSlug, currentUserLogin])

  const isNonDefault = canonicalFilterValue !== defaultFilterQuery

  const handleResetToDefault = useCallback(() => {
    setFilterValue(defaultFilterQuery)
    applyFilters(parseFilterQuery(defaultFilterQuery))
  }, [applyFilters, defaultFilterQuery])

  // --- Tab click handler ---

  const handleTabClick = useCallback(
    (tab: ActiveTab) => {
      const parsed = currentFilters({status: tab, detailedStatuses: []})
      setFilterValue(
        buildFilterQuery(
          tab,
          parsed.creators,
          parsed.sort,
          parsed.freeText,
          [],
          parsed.agents,
          currentUserLogin,
          parsed.excludedCreators,
          parsed.types,
        ),
      )
      applyFilters(parsed)
    },
    [currentFilters, applyFilters, currentUserLogin],
  )

  // --- Dropdown change handlers ---

  const handleStatusChange = useCallback(
    (statuses: string[]) => applyFilters(currentFilters({detailedStatuses: statuses})),
    [currentFilters, applyFilters],
  )

  const handleSortChange = useCallback(
    (direction: SortDirection) => applyFilters(currentFilters({sort: direction})),
    [currentFilters, applyFilters],
  )

  const handleCreatorChange = useCallback(
    (creators: string[]) => applyFilters(currentFilters({creators})),
    [currentFilters, applyFilters],
  )

  const handleAgentChange = useCallback(
    (agents: string[]) => applyFilters(currentFilters({agents})),
    [currentFilters, applyFilters],
  )

  const handleTypeChange = useCallback(
    (types: string[]) => applyFilters(currentFilters({types})),
    [currentFilters, applyFilters],
  )

  // --- Navigation detection ---
  // Track URL param changes to show skeleton loaders during transitions
  const currentPage = parsePageNumber(searchParams.get('page'))
  const currentQ = searchParams.get('q')
  const searchParamsKey = searchParams.toString()
  const [prevPage, setPrevPage] = useState(currentPage)
  const [prevTab, setPrevTab] = useState(activeTab)
  const [prevQ, setPrevQ] = useState(currentQ)
  const [prevParamsKey, setPrevParamsKey] = useState(searchParamsKey)

  // In q= mode, track q/page/tab changes. In legacy mode, track any param change.
  const isNavigating = useQSyntax
    ? prevPage !== currentPage || prevTab !== activeTab || prevQ !== currentQ
    : prevParamsKey !== searchParamsKey || prevTab !== activeTab

  if (!isFetching && isNavigating) {
    setPrevPage(currentPage)
    setPrevTab(activeTab)
    setPrevQ(currentQ)
    setPrevParamsKey(searchParamsKey)
  }

  // --- Server-side filtering: tasks come pre-filtered from API ---
  // No client-side filtering needed — API handles tab, status, author, agent, sort, search

  const hasActiveFilters =
    searchQuery !== '' ||
    sortDirection !== 'newest' ||
    selectedCreators.length > 0 ||
    excludedCreators.length > 0 ||
    selectedAgents.length > 0 ||
    selectedTypes.length > 0 ||
    selectedStatuses.length > 0

  return {
    // Filter bar state
    filterValue,
    filterProviders,
    isNonDefault,

    // Dropdown state
    sortDirection,
    creatorOptions,
    selectedCreators,
    selectedStatuses,
    agentOptions,
    selectedAgents,
    selectedTypes,

    // Handlers
    handleFilterChange,
    handleFilterSubmit,
    handleResetToDefault,
    handleTabClick,
    handleSortChange,
    handleCreatorChange,
    handleStatusChange,
    handleAgentChange,
    handleTypeChange,

    // Parsed values (for passing to useTaskFilterParams)
    searchQuery,

    // Navigation
    isNavigating,
    hasActiveFilters,
  }
}
